// Regex-based export extraction for JS/TS files (fallback when tree-sitter unavailable)

export interface ModuleExport {
  kind: "fn" | "class" | "type" | "const" | "method";
  name: string;
  /** Condensed param list for functions, type annotation for consts */
  detail: string;
  /** Return type if extractable */
  returns?: string;
  /** For types/interfaces: field list */
  fields?: string[];
  /** Parent class name (for methods) */
  parent?: string;
}

export function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 3) + "..." : s;
}

export function extractExports(content: string): ModuleExport[] {
  const exports: ModuleExport[] = [];
  const lines = content.split("\n");
  let currentClass: string | null = null;
  let currentInterface: { name: string; fields: string[] } | null = null;
  let braceDepth = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Track brace depth for class/interface body detection
    if (currentClass || currentInterface) {
      braceDepth += (line.match(/\{/g) || []).length;
      braceDepth -= (line.match(/\}/g) || []).length;
      if (braceDepth <= 0) {
        currentClass = null;
        currentInterface = null;
        braceDepth = 0;
        continue;
      }
    }

    // Inside a class: extract methods
    if (currentClass) {
      const methodMatch = line.match(/^\s+(?:async\s+)?(\w+)\s*(\([^)]*\))\s*(?::\s*([\w<>[\]|& ]+))?/);
      if (methodMatch && !line.includes("private") && !line.includes("#") && methodMatch[1] !== "constructor") {
        let params = methodMatch[2];
        if (params.length > 50) params = params.slice(0, 47) + "...";
        const returns = methodMatch[3] ? truncate(methodMatch[3], 30) : undefined;
        exports.push({ kind: "method", name: methodMatch[1], detail: params, returns, parent: currentClass });
      }
      continue;
    }

    // Inside an interface/type: extract fields
    if (currentInterface) {
      const fieldMatch = line.match(/^\s+(?:readonly\s+)?(\w+)\??\s*:\s*(.+?)(?:;|\s*$)/);
      if (fieldMatch && currentInterface.fields.length < 8) {
        const fieldType = truncate(fieldMatch[2].replace(/;$/, "").trim(), 40);
        currentInterface.fields.push(`${fieldMatch[1]}: ${fieldType}`);
      }
      continue;
    }

    // export (async) function NAME(params...): ReturnType
    const fnMatch = line.match(/^export\s+(?:async\s+)?function\s+(\w+)\s*(\([^)]*\))\s*(?::\s*([\w<>[\]|& ]+))?/);
    if (fnMatch) {
      let params = fnMatch[2];
      if (params.length > 60) params = params.slice(0, 57) + "...";
      const returns = fnMatch[3] ? truncate(fnMatch[3], 30) : undefined;
      exports.push({ kind: "fn", name: fnMatch[1], detail: params, returns });
      continue;
    }

    // export const NAME = (async)?(params): ReturnType =>
    const arrowMatch = line.match(
      /^export\s+const\s+(\w+)\s*=\s*(?:async\s+)?\(([^)]*)\)\s*(?::\s*([\w<>[\]|& ]+))?\s*=>/
    );
    if (arrowMatch) {
      let params = `(${arrowMatch[2]})`;
      if (params.length > 60) params = params.slice(0, 57) + "...";
      const returns = arrowMatch[3] ? truncate(arrowMatch[3], 30) : undefined;
      exports.push({ kind: "fn", name: arrowMatch[1], detail: params, returns });
      continue;
    }

    // export class NAME — start tracking methods
    const classMatch = line.match(/^export\s+(?:abstract\s+)?class\s+(\w+)/);
    if (classMatch) {
      currentClass = classMatch[1];
      braceDepth = (line.match(/\{/g) || []).length - (line.match(/\}/g) || []).length;
      exports.push({ kind: "class", name: classMatch[1], detail: "" });
      continue;
    }

    // export (interface|type) NAME — start tracking fields
    const typeMatch = line.match(/^export\s+(?:interface|type)\s+(\w+)/);
    if (typeMatch) {
      const fields: string[] = [];
      exports.push({ kind: "type", name: typeMatch[1], detail: "", fields });
      if (line.includes("{")) {
        currentInterface = { name: typeMatch[1], fields };
        braceDepth = (line.match(/\{/g) || []).length - (line.match(/\}/g) || []).length;
        if (braceDepth <= 0) currentInterface = null;
      }
      continue;
    }

    // export const NAME (non-arrow)
    const constMatch = line.match(/^export\s+const\s+(\w+)\s*(?::\s*([\w<>[\]|& ,]+?))?\s*=/);
    if (constMatch) {
      const typePart = constMatch[2] ? `: ${constMatch[2].trim()}` : "";
      exports.push({ kind: "const", name: constMatch[1], detail: typePart });
      continue;
    }

    // export { name1, name2 } or export { name1 as alias } from './module'
    const reExportMatch = line.match(/^export\s+\{([^}]+)\}/);
    if (reExportMatch) {
      for (const part of reExportMatch[1].split(",")) {
        const segments = part.trim().split(/\s+as\s+/);
        const name = (segments.length > 1 ? segments[1] : segments[0])?.trim();
        if (name && name !== "default") {
          exports.push({ kind: "const", name, detail: "(re-export)" });
        }
      }
    }
  }

  return exports;
}

/** Extract import specifiers from a file (for reference counting) */
export function extractImports(content: string): string[] {
  const imports: string[] = [];
  const patterns = [
    /import\s+(?:type\s+)?(?:\{[^}]*\}|\w+)(?:\s*,\s*\{[^}]*\})?\s+from\s+["']([^"']+)["']/g,
    /require\(["']([^"']+)["']\)/g,
  ];
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(content)) !== null) {
      imports.push(match[1]);
    }
    pattern.lastIndex = 0;
  }
  return imports;
}
